<template>
  <div v-if="!loading" class="space-y-8">
    <div class="flex space-x-4">
      <div class="flex glue-h items-baseline">
        <div v-text="periodRange" class="input cursor-default select-none" />
        <DropdownDateRangePicker @changed="period = $event" />
      </div>
      <div class="select-wrapper space-y-2">
        <select class="select" placeholder="Select category" v-model="pageId">
          <option
            v-for="campaignPage in campaignPages"
            :key="campaignPage.id"
            :value="campaignPage.id"
          >
            {{ campaignPage.title }}
          </option>
        </select>
      </div>
      <div class="glue-h">
        <template v-if="pageId">
          <div class="select-wrapper space-y-2">
            <select class="select" placeholder="Select category" v-model="dimension">
              <option v-for="dim in dimensions" :key="dim.value" :value="dim.value">
                {{ dim.name }}
              </option>
            </select>
          </div>
        </template>
      </div>
      <button
        v-if="pageId"
        class="btn btn-success"
        :disabled="loading || fetching"
        @click="handleGetPageGoogleAnalytics"
      >
        Submit
      </button>
    </div>

    <div>
      <DataIterat :headless="fetching" :items="rows" :meta="metas" :loading="fetching">
        <div slot="list-header-content" class="flex -mx-3">
          <div
            v-for="(col, index) in header"
            :key="`header_${index}`"
            :class="{ 'w-1/4': !index, 'flex-1': index, 'text-center': index }"
            class="px-3 text-xs leading-normal"
          >
            <p class="text-base">
              {{ col.alias | capitalize }}
            </p>
            <p v-if="!index">
              {{ col.description }}
            </p>
          </div>
        </div>
        <template #list-item-content="{ dataItem: row }">
          <div class="flex -mx-3">
            <div
              v-for="(col, index) in row"
              :key="`row_${index}`"
              :class="{ 'w-1/4': !index, 'flex-1': index, 'text-center': index }"
              class="px-3 text-sm"
            >
              {{ col }}
            </div>
          </div>
        </template>
      </DataIterat>
    </div>
  </div>
</template>

<script>
import { middleware } from '@/routes/guards';
import { campaignPageComputed, campaignPageMethods } from '@/store/helper';
import pageGoogleAnalyticsClient from '@/api/clients/pageGoogleAnalyticsClient';
import DropdownDateRangePicker from '@/components/DropdownDateRangePicker.vue';
import DataIterat from '@/components/Ui/DataIterat.vue';

export default {
  name: 'GoogleAnalyticsSingle',
  components: {
    DropdownDateRangePicker,
    DataIterat,
  },
  props: {
    campaignId: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    loading: false,
    fetching: false,
    pageId: null,
    dimensions: [
      {
        name: 'Date',
        value: 'ga:date',
      },
      {
        name: 'Age',
        value: 'ga:userAgeBracket',
      },
      {
        name: 'Gender',
        value: 'ga:userGender',
      },
      {
        name: 'Affinity Category (reach)',
        value: 'ga:interestAffinityCategory',
      },
      {
        name: 'In-market segments',
        value: 'ga:interestInMarketCategory',
      },
      {
        name: 'Language',
        value: 'ga:language',
      },
      {
        name: 'Country',
        value: 'ga:country',
      },
      {
        name: 'City',
        value: 'ga:city',
      },
      {
        name: 'User Type',
        value: 'ga:userType',
      },
      {
        name: 'Count of sessions',
        value: 'ga:sessionCount',
      },
      {
        name: 'Session duration',
        value: 'ga:sessionDurationBucket',
      },
      {
        name: 'Browser',
        value: 'ga:browser',
      },
      {
        name: 'Operating System',
        value: 'ga:operatingSystem',
      },
      {
        name: 'Screen Resolution',
        value: 'ga:screenResolution',
      },
      {
        name: 'Service Provider',
        value: 'ga:networkLocation',
      },
      {
        name: 'Device Category',
        value: 'ga:deviceCategory',
      },
      {
        name: 'Mobile Device Info',
        value: 'ga:mobileDeviceInfo',
      },
      {
        name: 'Default Channel Grouping',
        value: 'ga:channelGrouping',
      },
      {
        name: 'Source / Medium',
        value: 'ga:sourceMedium',
      },
      {
        name: 'Source',
        value: 'ga:source',
      },
      {
        name: 'Medium',
        value: 'ga:medium',
      },
    ],
    dimension: 'ga:date',
    period: null,
    header: null,
    rows: [],
    metas: {},
  }),
  computed: {
    ...campaignPageComputed,
    periodRange() {
      if (!this.period) return '';
      return `${this.period.since} - ${this.period.until}`;
    },
  },
  beforeRouteEnter: middleware('account-permission:read pages'),
  created() {
    this.handleGetPages();
  },
  destroyed() {
    this.setIds([]);
  },
  methods: {
    ...campaignPageMethods,
    handleGetPages() {
      const { campaignId } = this;
      this.loading = true;
      this.getCampaignPages({ campaignId })
        .then(() => {
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    handleGetPageGoogleAnalytics() {
      const { pageId, dimension, period } = this;

      const queryParams = {
        dimension,
        period,
      };

      this.fetching = true;
      return pageGoogleAnalyticsClient
        .getPageGoogleAnalytics(pageId, queryParams)
        .then(({ status = null, data = null }) => {
          if (status === 200 && data) {
            const { header, rows, meta } = data;
            this.header = header;
            this.rows = rows;
            this.metas = meta;
          }
          this.fetching = false;
        })
        .catch(() => {
          this.fetching = false;
        });
    },
  },
};
</script>
